import { useState, useEffect, useCallback, useRef } from 'react';
import { traceStart, traceEnd, traceError, addBreadcrumb } from '../../lib/telemetry';

/**
 * Shared paging + request-sequencing engine for the marketplace search hooks.
 *
 * Two things here are load-bearing and were each a real bug:
 *
 * 1. The page resets in the SAME render the search parameters change, because it
 *    is stored alongside the key of the parameters it belongs to. A reset done
 *    one render later (via an effect) lets a doomed request go out at the
 *    previous page's offset first — on 2026-08-26 the offset:60 response for
 *    "opportunity" beat the offset:0 one back and the store rendered results
 *    61-73, hiding an arche that actually ranked first.
 *
 * 2. Only the most recently started request may write state, and it is the only
 *    one allowed to clear the spinner. Requests still overlap legitimately
 *    (rapid page clicks, refresh during an in-flight search) and each one costs
 *    a server-side embedding call, so completion order is not request order. The
 *    superseded request is also aborted, which is what actually saves the call.
 */
export interface PagedSearchTelemetry<TResult> {
  /** Trace name, e.g. 'marketplaceSearch'. */
  traceName: string;
  /** Breadcrumb text for a started request, e.g. 'Starting search'. */
  startMessage: string;
  /** Breadcrumb text for a completed request. */
  resultMessage: (result: TResult) => string;
  /** Extra fields recorded on the completion breadcrumb + trace. */
  resultDetails: (result: TResult) => Record<string, unknown>;
  /** Breadcrumb text for a failed request. */
  failureMessage: (errorMessage: string) => string;
}

export interface UsePagedSearchOptions<TResult> {
  /**
   * Stable identity of every parameter that should send the user back to page 1.
   * Build it from primitives (see `buildSearchKey`) — never from object identity,
   * or a caller passing an inline array re-fires the request on every render.
   */
  searchKey: string;
  limit: number;
  /** Issues the request. Must honour `signal` so superseded work is cancelled. */
  request: (limit: number, offset: number, signal: AbortSignal) => Promise<TResult>;
  /** Parameters recorded in telemetry for this request. */
  telemetryParams: Record<string, unknown>;
  telemetry: PagedSearchTelemetry<TResult>;
  /** Message used when a thrown value is not an Error. */
  fallbackErrorMessage: string;
}

export interface UsePagedSearchReturn<TResult> {
  results: TResult | null;
  loading: boolean;
  error: string | null;
  page: number;
  setPage: (page: number) => void;
  refresh: () => void;
}

/**
 * Builds a stable search key from arbitrary parameters. Arrays are sorted so
 * that a caller reordering tags does not read as a different search.
 */
export function buildSearchKey(parts: ReadonlyArray<unknown>): string {
  return JSON.stringify(parts.map((part) => (Array.isArray(part) ? [...part].sort() : part)));
}

export function usePagedSearch<TResult>(
  options: UsePagedSearchOptions<TResult>,
): UsePagedSearchReturn<TResult> {
  const { searchKey, limit, request, telemetryParams, telemetry, fallbackErrorMessage } = options;

  const [results, setResults] = useState<TResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The page belongs to a specific parameter set — including the page size,
  // since a different `limit` makes a page number mean something else.
  const pageKey = `${searchKey}|${limit}`;
  const [pageState, setPageState] = useState({ key: pageKey, page: 1 });
  const page = pageState.key === pageKey ? pageState.page : 1;
  const setPage = useCallback(
    (nextPage: number) => {
      setPageState({ key: pageKey, page: nextPage });
    },
    [pageKey],
  );

  const offset = (page - 1) * limit;

  // Everything that is not part of the request's identity is read through a ref,
  // so an unmemoised callback or params object from a caller cannot retrigger
  // the fetch. The fetch depends on `pageKey` and `offset` and nothing else.
  const latestRef = useRef({ request, telemetryParams, telemetry, fallbackErrorMessage });
  useEffect(() => {
    latestRef.current = { request, telemetryParams, telemetry, fallbackErrorMessage };
  });

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const fetchResults = useCallback(async () => {
    const current = latestRef.current;
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestIdRef.current === requestId;

    // Cancel whatever this request supersedes; the server stops work we no
    // longer intend to render.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const searchParams = { ...current.telemetryParams, limit, offset };
    const traceId = traceStart(current.telemetry.traceName, {
      searchParams,
      apiBase: import.meta.env.VITE_API_URL || '/v1',
    });

    addBreadcrumb('marketplace', current.telemetry.startMessage, searchParams);

    try {
      const result = await current.request(limit, offset, controller.signal);
      const stale = !isCurrent();

      addBreadcrumb('marketplace', current.telemetry.resultMessage(result), {
        ...current.telemetry.resultDetails(result),
        stale,
      });

      traceEnd(traceId, { ...current.telemetry.resultDetails(result), stale });

      if (stale) return;

      setResults(result);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : current.fallbackErrorMessage;
      const stale = !isCurrent();

      traceError(traceId, err instanceof Error ? err : new Error(String(err)), {
        searchParams,
        stale,
        aborted: controller.signal.aborted,
      });
      addBreadcrumb('marketplace', current.telemetry.failureMessage(errorMsg), {
        stale,
        aborted: controller.signal.aborted,
      });

      // A superseded request's failure is not the user's problem — including the
      // abort we caused ourselves.
      if (stale || controller.signal.aborted) return;

      setError(errorMsg);
      setResults(null);
    } finally {
      // A superseded request must not clear the spinner for the one still running.
      if (isCurrent()) {
        setLoading(false);
      }
    }
  }, [limit, offset, pageKey]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Abandon any in-flight request when the consumer goes away.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { results, loading, error, page, setPage, refresh: fetchResults };
}
